import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function PrivacyModal() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOpen = location.pathname === '/privacy';
  const isEmbedded = window.self !== window.top; // Check if in iframe
  const currentUrl = window.location.href;


  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');
  
  const onClose = () => {
    // If in iframe, use postMessage to communicate with parent
    if (isEmbedded) {
      window.parent.postMessage({ type: 'PRIVACY_MODAL_CLOSED' }, '*');
    } else if (returnUrl) {
      // If returnUrl is provided, navigate there
      window.location.href = returnUrl;
    } else {
      // Otherwise use regular navigation
      navigate(-1);
    }
  };
  
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  // Listen for messages from parent (Clerk) if in iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Verify origin for security if needed
      // if (event.origin !== 'https://accounts.boxyqr.com') return;
      
      if (event.data?.type === 'CLOSE_MODAL') {
        onClose();
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  if (!isOpen && !isEmbedded) return null;
  
  // Adjust styling based on whether it's embedded or not
  const containerStyle = isEmbedded ? {
    position: 'relative',
    height: '100vh',
    width: '100%',
    overflow: 'auto',
    background: 'white'
  } : {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '1rem'
  };
  
  return (    
    
    <div style={containerStyle}>
      <div className={isEmbedded ? "w-full h-full" : "bg-white rounded-lg p-6 max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto"}>
        {!isEmbedded && (
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-xl font-bold hover:text-gray-700"
            aria-label="Close"
          >
            ×
          </button>
        )}
        
        <h2 className="text-2xl font-bold mb-6">Privacy Policy</h2>
        
        <div className="prose prose-sm max-w-none">
          <h3 className="text-xl font-semibold mt-4 mb-2">Last Updated: April 21, 2025</h3>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">1. Introduction</h3>
          <p>
            This Privacy Policy describes how BoxyQR ("we," "us," or "our") collects, uses, and shares information in connection with your use of our websites, applications, and other services (collectively, the "Services"). This Privacy Policy applies to information we collect when you use our Services or when you otherwise interact with us.
          </p>
          <p>
            We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the policy and, in some cases, we may provide you with additional notice (such as adding a statement to our website or sending you a notification).
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">2. Information We Collect</h3>
          
          <h4 className="text-md font-semibold mt-3 mb-1">2.1 Information You Provide to Us</h4>
          <p>We collect information you provide directly to us, including:</p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Account Information</strong>: When you register for an account, we collect your name, email address, password, and other information you provide during the registration process.</li>
            <li><strong>Billing Information</strong>: If you purchase a paid plan, we collect payment and billing information, including credit card details and billing address.</li>
            <li><strong>QR Code Data</strong>: We collect information related to the QR codes you create, including the target URLs, names, and other settings.</li>
            <li><strong>Communications</strong>: When you contact us directly, we record the communications and the information provided in those communications.</li>
          </ul>
          
          <h4 className="text-md font-semibold mt-3 mb-1">2.2 Information We Collect Automatically</h4>
          <p>When you access or use our Services, we automatically collect information about you, including:</p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Log Information</strong>: We collect log information about your use of the Services, including the type of browser you use, access times, pages viewed, your IP address, and the page you visited before navigating to our Services.</li>
            <li><strong>Device Information</strong>: We collect information about the device you use to access our Services, including the hardware model, operating system and version, unique device identifiers, and mobile network information.</li>
            <li><strong>Usage Information</strong>: We collect information about your use of the Services, such as the QR codes you create, scan events, and other actions you take within the Services.</li>
            <li><strong>Location Information</strong>: We may collect information about your location when you access or use our Services, including general location information (such as city and country) based on your IP address.</li>
          </ul>
          
          <h4 className="text-md font-semibold mt-3 mb-1">2.3 Camera Access and Image Processing</h4>
          <p>
            Our AI Scanner feature requires access to your device's camera to scan QR codes, product IDs, SNS codes, and other identifiers from images. We only access your camera when you explicitly use the scanning feature, and we use your data as follows:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Local Storage</strong>: Images you capture or upload are initially processed and stored locally on your device.</li>
            <li><strong>Google Vision API</strong>: To extract text and information from your images, we transmit image data to Google's Vision API. This third-party service helps us accurately identify and extract the information you request. Google may temporarily store this data according to their privacy policy.</li>
            <li><strong>Data Extraction</strong>: The text, codes, or identifiers extracted from your images are processed to provide you with the requested information and to enable CSV export functionality.</li>
            <li><strong>Local Results Storage</strong>: The extracted data and results are stored locally on your device to enable CSV export and bulk QR code generation.</li>
            <li><strong>No Persistent Server Storage</strong>: We do not permanently store your images on our servers. Once the Google Vision API has processed the image and returned the extracted data, we do not retain copies of the original images.</li>
            <li><strong>No Monitoring</strong>: We do not access your camera when you are not actively using the scanning feature.</li>
          </ul>
          <p>
            By using our AI Scanner feature, you acknowledge that image data will be transmitted to Google's Vision API for processing. You can revoke camera access permissions at any time through your device settings. Please note that without camera access, the scanning functionality will be limited to uploading existing images from your device.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">3. Cookie Usage and GDPR Compliance</h3>
          <p>
            Our website uses cookies and similar technologies to enhance your experience, analyze usage, and assist in our marketing efforts. We use Cookiebot to manage cookie consent and provide transparency about data collection practices in compliance with GDPR and other privacy regulations.
          </p>
          <p>
            You can review and modify your cookie preferences at any time through the Cookie Declaration accessible on our website. For more information about how we use cookies, please visit our Cookie Policy page.
          </p>
          <p>
            For users in the European Economic Area (EEA), we process your personal data in accordance with the General Data Protection Regulation (GDPR). This includes:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Processing your data only with a lawful basis</li>
            <li>Implementing appropriate technical and organizational measures to protect your data</li>
            <li>Respecting your rights to access, correct, delete, or transfer your personal data</li>
            <li>Obtaining consent before collecting certain types of data</li>
          </ul>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">4. Third-Party Service Providers</h3>
          <p>
            We use trusted third-party service providers to help us operate our Services, including:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Cloud database services to securely store your information</li>
            <li>Authentication and user management services to protect your account</li>
            <li>Payment processors to handle subscription billing</li>
            <li>Image processing services to enable our AI Scanner functionality</li>
          </ul>
          <p>
            These service providers are contractually obligated to use your information only for the purposes of providing services to us and in accordance with this Privacy Policy. We do not sell your personal information to third parties.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">5. Children's Privacy</h3>
          <p>
            Our Services are not directed to children under 16 years of age. We do not knowingly collect personal information from children under 16. If we learn that we have collected personal information from a child under 16, we will promptly delete that information. If you believe we might have any information from or about a child under 16, please contact us at support@boxyqr.com.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">6. QR Code Analytics and Data Collection</h3>
          <h4 className="text-md font-semibold mt-3 mb-1">6.1 Scan Data and IP Anonymization</h4>
          <p>
            When someone scans a QR code or visits a shortened link created through our Services, we collect certain information to provide analytics and improve our service. This includes:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>Anonymized IP Address</strong>: We automatically anonymize all IP addresses by removing the last octet (the last part of the IP address). For example, an IP address like 104.178.15.80 is stored as 104.178.15.0. This prevents us from identifying individual users while still allowing for general geographical statistics.</li>
            <li><strong>Device Information</strong>: We collect information about the type of device used (mobile, desktop, tablet) and browser information.</li>
            <li><strong>Location Data</strong>: Using the IP address (before anonymization), we determine general geographic information such as country, region, and city. This location data is stored separately from the anonymized IP address.</li>
            <li><strong>Time and Date</strong>: We record when each scan or click occurs.</li>
            <li><strong>Referrer Information</strong>: We may collect information about the website that referred you to our service.</li>
          </ul>
          <p>
            This data is used to provide analytics to our users about their QR codes and shortened links. We do not attempt to identify individual users from this information, and we do not combine this data with other information that could lead to identification of individuals.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">6.2 Data Retention</h4>
          <p>
            For users on the free plan, QR codes and scan data will be active and stored for 14 days from creation. After this period, the QR codes will be deactivated, but the data may be retained in an anonymized format.
          </p>
          <p>
            For users on paid plans, QR codes and scan data will be stored for the duration specified in your subscription plan.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">7. Data Security and Breach Notification</h3>
          <p>
            We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
          </p>
          <p>
            In the event of a data breach that compromises your personal information, we will notify you via email within 72 hours of discovering the breach. The notification will include:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>The nature of the breach</li>
            <li>The types of data potentially affected</li>
            <li>Steps we are taking to address the breach</li>
            <li>Recommendations for protecting yourself</li>
            <li>Contact information for questions</li>
          </ul>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">8. Updates and Notifications</h3>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Sending an email to the address associated with your account</li>
            <li>Posting a notice on our website</li>
            <li>Displaying a notification within the application</li>
          </ul>

          


          <h3 className="text-lg font-semibold mt-4 mb-2">8.1 Account Deletion and Data Removal</h3>
          <p>
          When you delete your account, we will:          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>- Remove all personal information associated with your account</li>
            <li>- Delete all your QR codes and shortened links</li>
            <li>- Delete all scan records associated with your QR codes and shortened links
            </li>
            <li>- Delete all usage data linked to your account
            </li>
          </ul>
          <p>
          We may retain certain anonymized statistical information that cannot be linked back to you or your account. This data is used only for analytics purposes to help us improve our services.         </p>
          <p>
          If there are legal requirements that prevent us from deleting certain information (such as financial records for tax purposes), we will only retain the specific data required by law for the minimum time required. </p>
          <p>
            
            The date at the top of this document indicates when it was last updated. Your continued use of the Services after such modifications constitutes your acceptance of the revised Policy.
          </p>
          



          <h3 className="text-lg font-semibold mt-4 mb-2">Contact Us</h3>
          <p>
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <p>
            Email: support@boxyqr.com<br />
            BoxyQR<br />
            San Francisco, CA<br />
            United States
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyModal;